import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import StoreBadges from "@/components/site/StoreBadges";
import { Input } from "@/components/ui/input";
import { useAppModal } from "@/context/AppModalContext";
import { useLeadForm } from "@/hooks/useLeadForm";
import { useJsonLd } from "@/hooks/useJsonLd";
import { breadcrumbSchema } from "@/lib/seo";
import { SITE } from "@/constants/testIds";

const KICKER_TO_CRUMB = {
  accused: "Falsely accused",
  missing: "If you go missing",
  altercations: "Altercations",
};

const ALL_SITUATIONS = [
  {
    slug: "accused",
    kicker: "Falsely accused",
    title: "Their story is getting better. Yours is sealed.",
    to: "/accused",
  },
  {
    slug: "missing",
    kicker: "If you go missing",
    title: "If you can't tell the story, your record still can.",
    to: "/missing",
  },
  {
    slug: "altercations",
    kicker: "Altercations",
    title: "When it turns, the record already exists.",
    to: "/altercations",
  },
];

/* , Final CTA shared across the 3 situations , */
function SituationFinalCta({ slug }) {
  const { openModal } = useAppModal();
  const form = useLeadForm({
    source: `situation-${slug}`,
    placement: `situation-${slug}-final`,
  });

  return (
    <section className="py-20 md:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.1] text-[color:var(--yn-ink)] max-w-3xl">
          Get protected before the moment.
        </h2>
        <p className="mt-4 text-lg text-[color:var(--yn-muted)] max-w-2xl">
          Install YeboNadi on the phone you actually carry. Two taps to install.
          One tap to record.
        </p>

        <div className="mt-8 space-y-6">
          <StoreBadges
            placement={`situation-${slug}-final`}
            appTestId={SITE.finalCtaAppStoreBadge}
            playTestId={SITE.finalCtaPlayStoreBadge}
          />

          <div className="flex items-center gap-3 max-w-md">
            <div className="h-px flex-1 bg-[color:var(--yn-border)]" />
            <span className="text-xs uppercase tracking-wider text-[color:var(--yn-muted)]">
              or
            </span>
            <div className="h-px flex-1 bg-[color:var(--yn-border)]" />
          </div>

          {form.status === "success" ? (
            <div
              className="max-w-md rounded-md border border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)] p-4 flex items-start gap-3"
              data-testid={SITE.situationFinalSuccess}
              role="status"
              aria-live="polite"
            >
              <CheckCircle2
                size={20}
                className="mt-0.5 shrink-0"
                style={{ color: "var(--yn-primary)" }}
              />
              <div>
                <p className="text-sm font-semibold text-[color:var(--yn-ink)]">
                  Check your inbox.
                </p>
                <p className="text-sm text-[color:var(--yn-muted)] mt-1">
                  We sent the App Store and Google Play links to your email.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={form.submit} className="max-w-md space-y-3" noValidate>
              <label
                htmlFor={`situation-${slug}-email`}
                className="block text-sm font-medium text-[color:var(--yn-ink)]"
              >
                Get protected, we&apos;ll send the link to your phone.
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  id={`situation-${slug}-email`}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => form.setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid={SITE.situationFinalEmailInput}
                  className="h-11 border-[color:var(--yn-border)] focus-visible:ring-[color:var(--yn-primary)]"
                />
                <button
                  type="submit"
                  disabled={form.status === "loading"}
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-white text-sm font-semibold bg-[color:var(--yn-primary)] hover:bg-[color:var(--yn-primary-hover)] transition-colors disabled:opacity-70 whitespace-nowrap"
                  data-testid={SITE.situationFinalEmailSubmit}
                >
                  {form.status === "loading" && <Loader2 size={16} className="animate-spin" />}
                  {form.status === "loading" ? "Sending…" : "Send link"}
                </button>
              </div>
              {form.status === "error" && (
                <p
                  className="text-sm text-red-600"
                  data-testid={SITE.situationFinalError}
                  role="alert"
                >
                  {form.errorMsg}
                </p>
              )}
            </form>
          )}

          <button
            type="button"
            onClick={() => openModal(`situation-${slug}-final`)}
            className="text-sm font-medium text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
          >
            Or open the install panel →
          </button>
        </div>
      </div>
    </section>
  );
}

/* , Cross-link strip , */
function CrossLinks({ slug }) {
  const others = ALL_SITUATIONS.filter((s) => s.slug !== slug);
  return (
    <section
      className="py-16 md:py-20 border-y border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)]"
      data-testid={SITE.situationCrossLinks}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
          Also for
        </p>
        <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-[color:var(--yn-ink)]">
          One product. Three moments.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {others.map((s) => (
            <Link
              key={s.slug}
              to={s.to}
              className="group rounded-2xl bg-white border border-[color:var(--yn-border)] p-6 sm:p-7 flex items-start justify-between gap-4 hover:border-[color:var(--yn-primary)] transition-colors"
            >
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
                  {s.kicker}
                </p>
                <p className="mt-2 font-heading text-lg sm:text-xl font-semibold text-[color:var(--yn-ink)] leading-snug">
                  {s.title}
                </p>
              </div>
              <ArrowRight
                size={20}
                className="mt-1 shrink-0 text-[color:var(--yn-primary)] transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* , Situation Page shell , */
/**
 * Shared shell for /accused /missing /altercations.
 *
 * props:
 *   slug           , 'accused' | 'missing' | 'altercations'
 *   kicker         , small label above the H1
 *   headline       , emotional H1
 *   narrative      , [p1, p2, p3] short paragraphs (incident → retelling → what changes)
 *   coversHeading  , heading for the "how YeboNadi covers this" section
 *   covers         , [{icon, title, body}] items, MUST be claims from the claims sheet
 *   miniDemo       , React node (a small screenshot/illustration)
 *   subsectionTitle, string
 *   subsectionBody , string or node
 *   footerNote     , optional support-resources sentence, ONLY used on /missing
 */
export default function SituationPage({
  slug,
  kicker,
  headline,
  narrative,
  coversHeading = "How YeboNadi covers this",
  covers,
  miniDemo,
  subsectionTitle,
  subsectionBody,
  footerNote,
}) {
  useJsonLd(
    `situation-${slug}-crumbs`,
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: KICKER_TO_CRUMB[slug] || kicker, path: `/${slug}` },
    ])
  );

  return (
    <div data-testid={SITE.situationPage} data-slug={slug}>
      {/* HERO, headline only. No links per the "no CTA links out" rule. */}
      <section className="pt-14 sm:pt-20 lg:pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            {kicker}
          </p>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[color:var(--yn-ink)] max-w-4xl">
            {headline}
          </h1>
        </div>
      </section>

      {/* NARRATIVE, 3 short paragraphs */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div
              className="lg:col-span-7 space-y-6 text-lg sm:text-xl leading-[1.6] text-[color:var(--yn-muted)]"
              data-testid={SITE.situationNarrative}
            >
              {narrative.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {miniDemo && (
              <div className="lg:col-span-5">{miniDemo}</div>
            )}
          </div>

          <div className="mt-10">
            <StoreBadges
              placement={`situation-${slug}-hero`}
              appTestId={SITE.heroAppStoreBadge}
              playTestId={SITE.heroPlayStoreBadge}
            />
          </div>
        </div>
      </section>

      {/* HOW YEBONADI COVERS THIS */}
      <section
        className="py-16 md:py-24 border-y border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
              What the app does
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[color:var(--yn-ink)]">
              {coversHeading}
            </h2>
          </div>
          <ul
            className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6"
            data-testid={SITE.situationCovers}
          >
            {covers.map((c) => (
              <li
                key={c.title}
                className="rounded-2xl bg-white border border-[color:var(--yn-border)] p-6 sm:p-7"
              >
                <c.icon size={22} style={{ color: "var(--yn-primary)" }} />
                <h3 className="mt-4 font-heading text-lg sm:text-xl font-semibold text-[color:var(--yn-ink)] leading-snug">
                  {c.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-muted)]">
                  {c.body}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SUBSECTION */}
      {subsectionTitle && (
        <section className="py-16 md:py-24" data-testid={SITE.situationSubsection}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[color:var(--yn-ink)]">
                {subsectionTitle}
              </h2>
              <div className="mt-5 text-[17px] leading-[1.75] text-[color:var(--yn-muted)] space-y-4">
                {subsectionBody}
              </div>
            </div>
          </div>
        </section>
      )}

      <CrossLinks slug={slug} />

      <SituationFinalCta slug={slug} />

      {footerNote && (
        <div className="pb-12 -mt-4">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-sm leading-relaxed text-[color:var(--yn-muted)] border-t border-[color:var(--yn-border)] pt-6">
              {footerNote}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
